import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import NoClientState from './NoClientState';
import api from '../../services/api';
import { isCampaignViewer } from './aivisRoles';
import { isSnapshotBuilding, snapshotRetryDelayMs } from '../../services/snapshotState';
import {
  DEFAULT_VISIBILITY_TIMEFRAME,
  promptSummaryCacheKey,
  VISIBILITY_TIMEFRAME_OPTIONS,
  visibilityDeltaDirection,
  visibilityDeltaTitle,
} from '../../services/visibilityRange';
import './ai-vis.css';

/* ── Icons ───────────────────────────────────────────────────────────────── */
function SortChevron({ active, dir }) {
  if (!active) {
    return (
      <span className="aivis-prompts-sort-icon" aria-hidden>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 1.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 7.5L6 10.5L9 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return (
    <span className="aivis-prompts-sort-icon aivis-prompts-sort-icon-active" aria-hidden>
      {dir === 'asc' ? (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

function SearchIcon() {
  return (
    <span className="aivis-prompts-search-icon" aria-hidden>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function CheckboxCell({ checked, onChange, ariaLabel }) {
  return (
    <span className="aivis-prompts-checkbox-glow">
      <input type="checkbox" className="aivis-prompts-checkbox" checked={checked} onChange={onChange} aria-label={ariaLabel} />
    </span>
  );
}

/* ── Add Prompt Modal ────────────────────────────────────────────────────── */
const MIN_PROMPT_LENGTH = 15;
const DEFAULT_TIMEFRAME = DEFAULT_VISIBILITY_TIMEFRAME;
const TIMEFRAME_OPTIONS = VISIBILITY_TIMEFRAME_OPTIONS;

function VisibilityDelta({ value, basis }) {
  if (typeof value !== 'number') {
    return null;
  }
  const direction = visibilityDeltaDirection(value);
  return (
    <span
      className={`aivis-visibility-delta aivis-visibility-delta--${direction}`}
      title={visibilityDeltaTitle(basis)}
    >
      {value > 0 ? '+' : ''}{value.toFixed(1)} pts
    </span>
  );
}

function AddPromptModal({ onClose, onCreated, campaignId, existingPrompts = [] }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const existingNorm = useMemo(
    () => new Set(existingPrompts.map((p) => (p.text || '').trim().toLowerCase())),
    [existingPrompts]
  );

  // Analyze each line in real time
  const analysis = useMemo(() => {
    const seen = new Set();
    return text.split('\n').map((raw) => {
      const trimmed = raw.trim();
      if (!trimmed) return null; // blank line — skip silently
      const norm = trimmed.toLowerCase();
      let issue = null;
      if (trimmed.length < MIN_PROMPT_LENGTH) {
        issue = `Too short (min ${MIN_PROMPT_LENGTH} chars)`;
      } else if (seen.has(norm)) {
        issue = 'Duplicate in this list';
      } else if (existingNorm.has(norm)) {
        issue = 'Already exists for this client';
      }
      seen.add(norm);
      return { text: trimmed, issue };
    });
  }, [text, existingNorm]);

  const valid   = analysis.filter((a) => a && !a.issue);
  const invalid = analysis.filter((a) => a && a.issue);
  const canAdd  = valid.length > 0;

  async function handleCreate() {
    if (!canAdd) return;
    setBusy(true); setErr('');
    try {
      await Promise.all(
        valid.map((v) =>
          api.post(`/aivis/campaigns/${campaignId}/prompts/`, { text: v.text })
        )
      );
      onCreated();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not add prompts.');
      setBusy(false);
    }
  }

  return (
    <div className="aivis-modal-overlay" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="aivis-modal aivis-org-modal" style={{ maxWidth: 560 }}>
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">Add Prompts</h2>
        </div>

        <div className="aivis-field" style={{ marginTop: 16 }}>
          <label>Prompts <span style={{ fontWeight: 400, color: 'var(--aivis-text-muted)' }}>— one per line</span></label>
          <textarea
            rows={8}
            autoFocus
            value={text}
            onChange={(e) => { setText(e.target.value); setErr(''); }}
            placeholder={`What's the best weighted blanket to buy?\nWhat's the safest weighted blanket for children?\nWhat's the best cooling weighted blanket?`}
            disabled={busy}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {/* Live validation feedback */}
        {analysis.some(Boolean) && (
          <div className="aivis-prompt-validation">
            {invalid.length > 0 && (
              <div className="aivis-prompt-validation-issues">
                {invalid.map((item, i) => (
                  <div key={i} className="aivis-prompt-validation-row aivis-prompt-validation-row--bad">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span className="aivis-prompt-validation-text" title={item.text}>{item.text.length > 60 ? item.text.slice(0, 60) + '…' : item.text}</span>
                    <span className="aivis-prompt-validation-reason">{item.issue}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="aivis-field-hint" style={{ marginTop: invalid.length ? 8 : 0 }}>
              {valid.length > 0
                ? <><strong>{valid.length}</strong> prompt{valid.length === 1 ? '' : 's'} will be added{invalid.length > 0 ? ` · ${invalid.length} skipped` : '.'}</>
                : <span style={{ color: 'var(--aivis-danger)' }}>No valid prompts to add.</span>
              }
            </p>
          </div>
        )}

        {err && <p className="aivis-auth-error" style={{ marginTop: 8 }}>{err}</p>}

        <div className="aivis-modal-actions aivis-modal-actions--split" style={{ marginTop: 20 }}>
          <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="aivis-btn-primary" style={{ flex: '0 0 70%' }}
            onClick={handleCreate} disabled={busy || !canAdd}>
            {busy ? 'Adding…' : valid.length > 1 ? `Add ${valid.length} Prompts` : 'Add Prompt'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Archive Confirm Modal ───────────────────────────────────────────────── */
function ArchiveModal({ count, onConfirm, onCancel }) {
  return (
    <div className="aivis-modal-overlay" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="aivis-modal aivis-org-modal" style={{ maxWidth: 420 }}>
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">Archive {count === 1 ? 'Prompt' : `${count} Prompts`}</h2>
        </div>
        <p style={{ margin: '16px 0 8px', color: 'var(--aivis-text-muted)', fontSize: 14, lineHeight: 1.6 }}>
          Archived prompts are <strong>excluded from future runs</strong> across all services.
          Their historical data is fully preserved and they can be unarchived at any time.
        </p>
        <div className="aivis-modal-actions aivis-modal-actions--split" style={{ marginTop: 24 }}>
          <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="aivis-btn-danger aivis-modal-btn-main" onClick={onConfirm}
            style={{ flex: '0 0 70%', background: 'var(--aivis-caution-ink)' }}>
            Archive
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Delete Confirm Modal ────────────────────────────────────────────────── */
function DeleteModal({ count, onConfirm, onCancel }) {
  const [preserveData, setPreserveData] = useState(true);

  return (
    <div className="aivis-modal-overlay" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="aivis-modal aivis-org-modal" style={{ maxWidth: 460 }}>
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">Delete {count === 1 ? 'Prompt' : `${count} Prompts`}</h2>
        </div>
        <p style={{ margin: '16px 0 12px', color: 'var(--aivis-text-muted)', fontSize: 14, lineHeight: 1.6 }}>
          Choose what happens to the historical data collected for {count === 1 ? 'this prompt' : 'these prompts'}:
        </p>
        <div className="aivis-prompts-delete-options">
          <label className={`aivis-prompts-delete-option${preserveData ? ' aivis-prompts-delete-option--selected' : ''}`}>
            <input type="radio" name="del_opt" checked={preserveData} onChange={() => setPreserveData(true)} />
            <div>
              <strong>Preserve data</strong>
              <span>Keep all historical runs, responses and sources. Only the prompt definition is removed.</span>
            </div>
          </label>
          <label className={`aivis-prompts-delete-option${!preserveData ? ' aivis-prompts-delete-option--selected' : ''}`}>
            <input type="radio" name="del_opt" checked={!preserveData} onChange={() => setPreserveData(false)} />
            <div>
              <strong>Delete everything</strong>
              <span>Also delete all runs, responses and sources linked to {count === 1 ? 'this prompt' : 'these prompts'}. This cannot be undone.</span>
            </div>
          </label>
        </div>
        <div className="aivis-modal-actions aivis-modal-actions--split" style={{ marginTop: 24 }}>
          <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="aivis-btn-danger aivis-modal-btn-main" onClick={() => onConfirm(preserveData)}
            style={{ flex: '0 0 70%' }}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Unarchive Confirm Modal ─────────────────────────────────────────────── */
function UnarchiveModal({ count, onConfirm, onCancel }) {
  return (
    <div className="aivis-modal-overlay" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="aivis-modal aivis-org-modal" style={{ maxWidth: 420 }}>
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">Unarchive {count === 1 ? 'Prompt' : `${count} Prompts`}</h2>
        </div>
        <p style={{ margin: '16px 0 8px', color: 'var(--aivis-text-muted)', fontSize: 14, lineHeight: 1.6 }}>
          {count === 1 ? 'This prompt' : 'These prompts'} will be <strong>restored to active</strong> and included in future runs again.
        </p>
        <div className="aivis-modal-actions aivis-modal-actions--split" style={{ marginTop: 24 }}>
          <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="aivis-btn-primary aivis-modal-btn-main" onClick={onConfirm}
            style={{ flex: '0 0 70%', width: 'auto' }}>
            Unarchive
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function PromptsPage() {
  const { selectedCampaignId, campaigns, user } = useOutletContext();
  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));
  // Campaign viewers (clients) get a read-only Prompts page: no add, archive, or delete.
  const canManagePrompts = !isCampaignViewer(user);

  const [showArchived, setShowArchived] = useState(false);

  const [rows, setRows] = useState([]);
  const [comparisonBasis, setComparisonBasis] = useState(null);
  const [topicVisibility, setTopicVisibility] = useState(0);
  const [count, setCount] = useState(0);
  const [summaryStatus, setSummaryStatus] = useState('idle');
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME);
  const [timeframeReady, setTimeframeReady] = useState(false);
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const summaryCacheRef = useRef(new Map());
  const timeframeRef = useRef(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('visibility_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [showAdd, setShowAdd] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showUnarchive, setShowUnarchive] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    setTimeframeReady(false);
    if (!selectedCampaignId) {
      setTimeframe(DEFAULT_TIMEFRAME);
      setTimeframeReady(true);
      return;
    }
    try {
      const saved = localStorage.getItem(`aivis-prompts-timeframe:${selectedCampaignId}`);
      setTimeframe(TIMEFRAME_OPTIONS.some(([key]) => key === saved) ? saved : DEFAULT_TIMEFRAME);
    } catch {
      setTimeframe(DEFAULT_TIMEFRAME);
    }
    setTimeframeReady(true);
  }, [selectedCampaignId]);

  const load = useCallback(() => {
    summaryCacheRef.current.clear();
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  useEffect(() => {
    if (!selectedCampaignId || !timeframeReady) {
      setRows([]);
      setComparisonBasis(null);
      setTopicVisibility(0);
      setCount(0);
      setSummaryStatus('idle');
      return undefined;
    }

    const cacheKey = promptSummaryCacheKey(selectedCampaignId, timeframe, showArchived);
    const cached = summaryCacheRef.current.get(cacheKey);
    let cancelled = false;
    let timer;
    let controller;
    let retryAttempt = 0;

    if (cached) {
      setRows(cached.prompts || []);
      setComparisonBasis(cached.comparison?.basis || null);
      setTopicVisibility(cached.topic_visibility_pct || 0);
      setCount(cached.prompts_count ?? cached.prompts?.length ?? 0);
      setSummaryStatus('ready');
    } else {
      setRows([]);
      setComparisonBasis(null);
      setTopicVisibility(0);
      setCount(0);
      setSummaryStatus('loading');
    }
    setError('');

    const poll = async () => {
      controller = new AbortController();
      try {
        const { data } = await api.get(
          `/aivis/campaigns/${selectedCampaignId}/prompts-summary/?range=${timeframe}&archived=${showArchived}`,
          { signal: controller.signal },
        );
        if (cancelled) return;
        if (isSnapshotBuilding(data)) {
          setRows([]);
          setComparisonBasis(null);
          setTopicVisibility(0);
          setCount(0);
          setSummaryStatus('building');
          const delay = Math.min(
            30000,
            snapshotRetryDelayMs(data) * (2 ** Math.min(retryAttempt, 3)),
          );
          retryAttempt += 1;
          timer = window.setTimeout(poll, delay);
          return;
        }
        summaryCacheRef.current.set(cacheKey, data);
        setRows(Array.isArray(data.prompts) ? data.prompts : []);
        setComparisonBasis(data.comparison?.basis || null);
        setTopicVisibility(typeof data.topic_visibility_pct === 'number' ? data.topic_visibility_pct : 0);
        setCount(data.prompts_count ?? (data.prompts?.length ?? 0));
        setSummaryStatus('ready');
        retryAttempt = 0;
        if (data.refreshing) {
          timer = window.setTimeout(poll, snapshotRetryDelayMs(data));
        }
      } catch (e) {
        if (!controller.signal.aborted && !cancelled) {
          setSummaryStatus(cached ? 'ready' : 'error');
          if (!cached) {
            setError(e.response?.data?.detail || 'Could not load prompts.');
          }
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      controller?.abort();
      window.clearTimeout(timer);
    };
  }, [selectedCampaignId, showArchived, timeframe, timeframeReady, reloadNonce]);

  useEffect(() => { setSelectedIds(new Set()); }, [selectedCampaignId, showArchived]);

  const snapshotPreparing = summaryStatus === 'loading' || summaryStatus === 'building';
  const timeframeLabel = TIMEFRAME_OPTIONS.find(([key]) => key === timeframe)?.[1] || 'selected timeframe';

  useEffect(() => {
    if (!timeframeOpen) return undefined;
    function onDown(event) {
      if (timeframeRef.current && !timeframeRef.current.contains(event.target)) {
        setTimeframeOpen(false);
      }
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setTimeframeOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [timeframeOpen]);

  function selectTimeframe(next) {
    setTimeframe(next);
    setTimeframeOpen(false);
    try {
      localStorage.setItem(`aivis-prompts-timeframe:${selectedCampaignId}`, next);
    } catch {
      // The selection still works when browser storage is unavailable.
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? rows.filter((r) => (r.text || '').toLowerCase().includes(q)) : rows.slice();
    const mul = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === 'text') return mul * String(a.text || '').localeCompare(String(b.text || ''));
      if (sortKey === 'visibility_pct') return mul * ((a.visibility_pct ?? -1) - (b.visibility_pct ?? -1));
      if (sortKey === 'response_runs') return mul * ((a.response_runs || 0) - (b.response_runs || 0));
      if (sortKey === 'sources_linked') return mul * ((a.sources_linked ?? 0) - (b.sources_linked ?? 0));
      return mul * ((a.sort_order ?? 0) - (b.sort_order ?? 0));
    });
    return list;
  }, [rows, search, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'text' ? 'asc' : 'desc'); }
  }

  function toggleRow(id) {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  function toggleAllVisible() {
    const ids = filtered.map((r) => r.id);
    const allOn = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    setSelectedIds(allOn ? new Set() : new Set(ids));
  }

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const selCount = selectedIds.size;

  /* ── Actions ──────────────────────────────────────────────────────────── */
  async function handleArchiveConfirm() {
    setActionBusy(true);
    try {
      await Promise.all(
        [...selectedIds].map((id) =>
          api.patch(`/aivis/campaigns/${selectedCampaignId}/prompts/${id}/archive/`, { is_archived: true })
        )
      );
      setSelectedIds(new Set());
      load();
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Archive failed.');
    } finally { setActionBusy(false); setShowArchive(false); }
  }

  async function handleUnarchiveConfirm() {
    setActionBusy(true);
    try {
      await Promise.all(
        [...selectedIds].map((id) =>
          api.patch(`/aivis/campaigns/${selectedCampaignId}/prompts/${id}/archive/`, { is_archived: false })
        )
      );
      setSelectedIds(new Set());
      load();
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Unarchive failed.');
    } finally { setActionBusy(false); setShowUnarchive(false); }
  }

  async function handleDeleteConfirm(preserveData) {
    setActionBusy(true);
    try {
      await Promise.all(
        [...selectedIds].map((id) =>
          api.delete(`/aivis/campaigns/${selectedCampaignId}/prompts/${id}/?preserve_data=${preserveData}`)
        )
      );
      setSelectedIds(new Set());
      load();
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Delete failed.');
    } finally { setActionBusy(false); setShowDelete(false); }
  }

  function handlePromptCreated(newPrompt) {
    setShowAdd(false);
    load();
  }

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-prompts-page">
      {/* Modals */}
      {showAdd && (
        <AddPromptModal
          campaignId={selectedCampaignId}
          existingPrompts={rows}
          onClose={() => setShowAdd(false)}
          onCreated={handlePromptCreated}
        />
      )}
      {showArchive && (
        <ArchiveModal count={selCount} onConfirm={handleArchiveConfirm} onCancel={() => setShowArchive(false)} />
      )}
      {showUnarchive && (
        <UnarchiveModal count={selCount} onConfirm={handleUnarchiveConfirm} onCancel={() => setShowUnarchive(false)} />
      )}
      {showDelete && (
        <DeleteModal count={selCount} onConfirm={handleDeleteConfirm} onCancel={() => setShowDelete(false)} />
      )}

      {/* Header */}
      <div className="aivis-prompts-page-header">
        <div>
          <h1 className="aivis-page-title">Prompts</h1>
          <p className="aivis-page-sub">
            {selected
              ? `Prompts and tracking for "${selected.name}". Select a row to open details.`
              : 'Select a client in the sidebar to view its prompts.'}
          </p>
        </div>
        {selectedCampaignId && (
          <div className="aivis-dash-header-actions">
            <div className="aivis-dash-timeframe" ref={timeframeRef}>
              <button
                type="button"
                className={`aivis-dash-timeframe-trigger${timeframeOpen ? ' aivis-dash-timeframe-trigger--open' : ''}`}
                onClick={() => setTimeframeOpen((open) => !open)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    setTimeframeOpen(true);
                  }
                }}
                aria-haspopup="listbox"
                aria-expanded={timeframeOpen}
              >
                <span className="aivis-dash-timeframe-label">Timeframe</span>
                <span className="aivis-dash-timeframe-value">{timeframeLabel}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {timeframeOpen && (
                <div className="aivis-dash-timeframe-menu" role="listbox" aria-label="Prompt timeframe">
                  {TIMEFRAME_OPTIONS.map(([value, label]) => {
                    const selectedOption = value === timeframe;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="option"
                        aria-selected={selectedOption}
                        className={`aivis-dash-timeframe-option${selectedOption ? ' aivis-dash-timeframe-option--selected' : ''}`}
                        onClick={() => selectTimeframe(value)}
                      >
                        <span>{label}</span>
                        {selectedOption && (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <div className="aivis-auth-error">{error}</div>}

      {!selectedCampaignId ? null : (
        <>
          <div className="aivis-prompts-toolbar">
            {/* Left: tab toggle + search */}
            <div className="aivis-prompts-toolbar-left">
              <div className="aivis-prompts-tab-toggle">
                {/* sliding pill — animates between the two tabs */}
                <div
                  className={`aivis-prompts-tab-slider${showArchived ? ' aivis-prompts-tab-slider--right' : ''}`}
                  aria-hidden
                />
                <button
                  type="button"
                  className={`aivis-prompts-tab${!showArchived ? ' aivis-prompts-tab--active' : ''}`}
                  onClick={() => setShowArchived(false)}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`aivis-prompts-tab${showArchived ? ' aivis-prompts-tab--active' : ''}`}
                  onClick={() => setShowArchived(true)}
                >
                  Archived
                </button>
              </div>
              <div className="aivis-prompts-search-wrap">
                <SearchIcon />
                <input
                  type="search"
                  className="aivis-prompts-search"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search prompts"
                />
              </div>
            </div>

            {/* Right: bulk actions (when selected) + visibility + add */}
            <div className="aivis-prompts-toolbar-right">
              {canManagePrompts && selCount > 0 && (
                <div className="aivis-prompts-bulk-actions">
                  <span className="aivis-prompts-bulk-count">{selCount} selected</span>
                  {showArchived ? (
                    /* Archived view — show Unarchive + Delete */
                    <>
                      <button
                        type="button"
                        className="aivis-prompts-bulk-btn aivis-prompts-bulk-btn--unarchive"
                        disabled={actionBusy}
                        onClick={() => setShowUnarchive(true)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                          <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" rx="1" /><line x1="12" y1="12" x2="12" y2="17" /><polyline points="9 14 12 17 15 14" />
                        </svg>
                        Unarchive
                      </button>
                      <button
                        type="button"
                        className="aivis-prompts-bulk-btn aivis-prompts-bulk-btn--delete"
                        disabled={actionBusy}
                        onClick={() => setShowDelete(true)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                          <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                        </svg>
                        Delete
                      </button>
                    </>
                  ) : (
                    /* Active view — show Archive + Delete */
                    <>
                      <button
                        type="button"
                        className="aivis-prompts-bulk-btn aivis-prompts-bulk-btn--archive"
                        disabled={actionBusy}
                        onClick={() => setShowArchive(true)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                          <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" rx="1" /><line x1="10" y1="12" x2="14" y2="12" />
                        </svg>
                        Archive
                      </button>
                      <button
                        type="button"
                        className="aivis-prompts-bulk-btn aivis-prompts-bulk-btn--delete"
                        disabled={actionBusy}
                        onClick={() => setShowDelete(true)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                          <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                        </svg>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}

              {!showArchived && (
                <div className="aivis-prompts-metrics">
                  <div className="aivis-prompts-metric">
                    <span className="aivis-prompts-metric-label">Visibility</span>
                    <span className="aivis-prompts-metric-value">
                      {snapshotPreparing ? '…' : `${Math.round(topicVisibility)}%`}
                    </span>
                  </div>
                </div>
              )}

              {!showArchived && canManagePrompts && (
                <button type="button" className="aivis-prompts-add-btn" onClick={() => setShowAdd(true)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Prompt
                </button>
              )}
            </div>
          </div>

          <div className="aivis-prompts-table-card">
            {snapshotPreparing && !rows.length ? (
              <p className="aivis-page-sub" style={{ padding: 24 }}>
                Preparing your {timeframeLabel.toLowerCase()} prompt report. Your historical data is safe and will appear here automatically.
              </p>
            ) : (
              <div className="aivis-prompts-table-scroll">
                <table className="aivis-prompts-table">
                  <thead>
                    <tr>
                      {canManagePrompts && (
                        <th className="aivis-prompts-th-check">
                          <CheckboxCell checked={allVisibleSelected} onChange={toggleAllVisible} ariaLabel="Select all visible prompts" />
                        </th>
                      )}
                      <th className="aivis-prompts-th-sort" scope="col">
                        <button type="button" className="aivis-prompts-th-btn" onClick={() => toggleSort('text')}>
                          Prompt <SortChevron active={sortKey === 'text'} dir={sortDir} />
                        </button>
                      </th>
                      <th className="aivis-prompts-th-sort aivis-prompts-th-num" scope="col">
                        <button type="button" className="aivis-prompts-th-btn" onClick={() => toggleSort('visibility_pct')}>
                          Visibility % <SortChevron active={sortKey === 'visibility_pct'} dir={sortDir} />
                        </button>
                      </th>
                      <th className="aivis-prompts-th-num" scope="col">ChatGPT</th>
                      <th className="aivis-prompts-th-num" scope="col">Google AI</th>
                      <th className="aivis-prompts-th-sort aivis-prompts-th-num" scope="col">
                        <button type="button" className="aivis-prompts-th-btn" onClick={() => toggleSort('response_runs')}>
                          Response runs <SortChevron active={sortKey === 'response_runs'} dir={sortDir} />
                        </button>
                      </th>
                      <th className="aivis-prompts-th-sort aivis-prompts-th-num aivis-prompts-th-right" scope="col">
                        <button type="button" className="aivis-prompts-th-btn" onClick={() => toggleSort('sources_linked')}>
                          Sources linked <SortChevron active={sortKey === 'sources_linked'} dir={sortDir} />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={canManagePrompts ? 7 : 6} className="aivis-prompts-empty">
                          {rows.length === 0
                            ? (showArchived
                                ? 'No archived prompts.'
                                : canManagePrompts
                                  ? 'No prompts yet. Click "Add Prompt" to get started.'
                                  : 'No prompts yet.')
                            : 'No prompts match your search.'}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((row) => (
                        <tr key={row.id} className={`aivis-prompts-tr${selectedIds.has(row.id) ? ' aivis-prompts-tr--selected' : ''}`}>
                          {canManagePrompts && (
                            <td className="aivis-prompts-td-check">
                              <span onClick={(e) => e.stopPropagation()}>
                                <CheckboxCell checked={selectedIds.has(row.id)} onChange={() => toggleRow(row.id)} ariaLabel="Select prompt" />
                              </span>
                            </td>
                          )}
                          <td className="aivis-prompts-td-prompt">
                            <Link to={`/ai-vis/prompts/${row.id}`} className="aivis-prompts-cell-link">
                              <span className="aivis-prompts-prompt-text">{row.text}</span>
                            </Link>
                          </td>
                          <td className="aivis-prompts-td-num">
                            <span className={`aivis-dash-prompt-vis${row.visibility_pct == null ? '' : row.visibility_pct >= 50 ? ' aivis-dash-prompt-vis--high' : row.visibility_pct >= 20 ? ' aivis-dash-prompt-vis--mid' : ' aivis-dash-prompt-vis--low'}`}>
                              {row.visibility_pct != null ? `${row.visibility_pct}%` : '—'}
                            </span>
                            {typeof row.delta_points === 'number' && (
                              <>
                                <br />
                                <VisibilityDelta value={row.delta_points} basis={comparisonBasis} />
                              </>
                            )}
                          </td>
                          <td className="aivis-prompts-td-num">
                            <span className="aivis-prompts-num">
                              {row.by_service?.chatgpt != null ? `${row.by_service.chatgpt}%` : '—'}
                            </span>
                            {typeof row.by_service_delta_points?.chatgpt === 'number' && (
                              <>
                                <br />
                                <VisibilityDelta value={row.by_service_delta_points.chatgpt} basis={comparisonBasis} />
                              </>
                            )}
                          </td>
                          <td className="aivis-prompts-td-num">
                            <span className="aivis-prompts-num">
                              {row.by_service?.google_ai_overviews != null ? `${row.by_service.google_ai_overviews}%` : '—'}
                            </span>
                            {typeof row.by_service_delta_points?.google_ai_overviews === 'number' && (
                              <>
                                <br />
                                <VisibilityDelta value={row.by_service_delta_points.google_ai_overviews} basis={comparisonBasis} />
                              </>
                            )}
                          </td>
                          <td className="aivis-prompts-td-num">
                            <span className="aivis-prompts-num">{row.response_runs ?? 0}</span>
                          </td>
                          <td className="aivis-prompts-td-num aivis-prompts-td-sources">
                            <span className="aivis-prompts-num">{(row.sources_linked ?? 0) === 0 ? '—' : row.sources_linked}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="aivis-prompts-footer">
            <span className="aivis-prompts-count">
              {count} {showArchived ? 'archived ' : ''}prompt{count === 1 ? '' : 's'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
